import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import InboxTemplateMenu from '@features/InboxTemplateMenu';
import CouponsMenu from '@features/CouponsMenu';
import PostDynamicVariables from '@features/PostDynamicVariables';
import { ModalConfirm } from '@components/Modal';
import { Input } from '@components/Input';
import { Select } from '@components/Select';
import TextEditor from '@components/TextEditor';
import {
    usePermissions,
    useTranslation,
    useYupValidationResolver,
    useChannels,
    useLocations,
    useWhatsAppBusinessTemplates,
} from '@hooks';
import { CHANNEL } from '@constants/channels';
import { RoutePath } from '@constants/routes';
import { MAX_LENGTH } from '@constants/campaigns';
import { INBOX_PLACEHOLDER_TOKENS, PLACEHOLDER_CONTEXT } from '@constants/placeholders';
import { alertActions, channelsActions, contactsActions } from '@actions';
import { ConversationStatus, ConversationProperty } from '@constants/conversations';
import { getUserContacts } from '@helpers/userName';
import { handleApiError } from '@helpers/api';
import {
    assignsLiveChatLocation,
    getChannelLinkScope,
    isChannelInLinkScope,
    liveChatServesLocation,
} from '@helpers/channels';
import { parseTipTapJSONToPlainText, parseTipTapJSONToHTML, testEditorLength } from '@helpers/text-editor';
import { conversationsService } from '@services';
import { Content, Modal } from './styles';

const InitConversation = ({ onInitConversationCallback }) => {
    const [resolveModal, setResolveModal] = useState({ open: false, conversations: [] });
    const [channelType, setChannelType] = useState(CHANNEL.SMS);

    const locations = useSelector(state => state.account?.account?.companies || []);
    const { hasAccess } = usePermissions();
    const couponsEnabled = useSelector(state => state.account?.account?.couponsEnabled) && hasAccess('coupons');
    const channels = useSelector(state => state.channels.data);
    const modal = useSelector(state => state.contacts.contactInitConversationModal);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { getSelectLocationsOptions, hasSingleLocation, singleLocationId } = useLocations();
    const { getChannelOptions, getAccessibleChannelIds, canInitiateConversation } = useChannels();
    const { getWhatsAppBusinessTemplatesOptionsByChannel } = useWhatsAppBusinessTemplates();

    const isSurveyContext = !!modal.ctx?.surveyResponseId;
    const locationsOptions = useMemo(() => {
        const filterCb = isSurveyContext ? c => modal.locations.includes(c.companyId) : undefined;
        return getSelectLocationsOptions(filterCb);
    }, [locations, modal.locations, isSurveyContext]);

    const autoSelectSingleLocation =
        hasSingleLocation && locationsOptions.some(group => group.children.some(c => c.value === singleLocationId));

    const InitConversationSchema = yup.object().shape({
        contactId: yup.string().required('validation.required'),
        message: yup.string().when('channelId', {
            is: channelId => channelId && channelType !== CHANNEL.WHATSAPP_BUSINESS,
            then: schema =>
                schema
                    .required('validation.required')
                    // The form stores TipTap JSON; parse to wire text before measuring.
                    .test('text-editor-length', `text-editor-length`, value =>
                        testEditorLength(
                            parseTipTapJSONToPlainText(value),
                            MAX_LENGTH[channelType],
                            INBOX_PLACEHOLDER_TOKENS,
                        ),
                    ),
            otherwise: schema => schema.notRequired(),
        }),
        channelId: yup.string().required('validation.required'),
        locationId: yup
            .mixed()
            .nullable()
            .test('required-for-live-chat', 'validation.required', function (value) {
                return !assignsLiveChatLocation(channels.find(c => c.id === this.parent.channelId)) || !!value;
            }),
        whatsAppTemplateId: yup.string().when('channelId', {
            is: channelId => channelId && channelType === CHANNEL.WHATSAPP_BUSINESS,
            then: schema => schema.required('validation.required'),
            otherwise: schema => schema.notRequired(),
        }),
        subject: yup.string().when('channelId', {
            is: () => channelType === CHANNEL.EMAIL,
            then: schema => schema.required('validation.required'),
            otherwise: schema => schema.notRequired(),
        }),
    });

    const resolver = useYupValidationResolver(InitConversationSchema);

    const {
        setValue,
        control,
        handleSubmit,
        watch,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm({
        mode: 'all',
        resolver,
        defaultValues: {
            contactId: getUserContacts(
                {
                    firstName: modal.ctx?.firstName,
                    lastName: modal.ctx?.lastName,
                    phoneNumber: modal.ctx?.phoneNumber,
                    email: modal.ctx?.email,
                    externalId: modal.ctx?.externalId,
                },
                '-',
            ),
            message: '',
            channelId: '',
            locationId: modal.locations.length === 1 ? modal.locations[0] : '',
            ...(modal.ctx?.surveyResponseId ? { surveyResponseId: modal.ctx.surveyResponseId } : {}),
            whatsAppTemplateId: '',
            subject: '',
        },
    });

    const selectedChannelId = watch('channelId');
    const locationId = watch('locationId');
    const selectedChannel = useMemo(
        () => channels.find(c => c.id === selectedChannelId),
        [channels, selectedChannelId],
    );

    const channelLocationsOptions = useMemo(() => {
        if (isSurveyContext || !selectedChannel) return locationsOptions;
        if (assignsLiveChatLocation(selectedChannel)) {
            return getSelectLocationsOptions(c => liveChatServesLocation(selectedChannel, c));
        }
        if (selectedChannel.locationId) {
            return getSelectLocationsOptions(c => c.companyId === selectedChannel.locationId);
        }
        if (selectedChannel.brandId) {
            return getSelectLocationsOptions(c => c.brand?.id === selectedChannel.brandId);
        }
        return locationsOptions;
    }, [locationsOptions, isSurveyContext, selectedChannel]);

    useEffect(() => {
        const flat = channelLocationsOptions.flatMap(group => group.children);
        if (flat.length === 1) {
            if (locationId !== flat[0].value) {
                setValue('locationId', flat[0].value, { shouldValidate: true });
            }
        } else if (locationId && !flat.some(o => o.value === locationId)) {
            setValue('locationId', '', { shouldValidate: true });
        }
    }, [channelLocationsOptions]);

    useEffect(() => {
        if (modal?.open) {
            dispatch(channelsActions.getWhatsAppBusinessTemplates());
        }
    }, [dispatch, modal?.open]);

    const channelsOptions = useMemo(() => {
        let filterCb = canInitiateConversation;
        if (isSurveyContext) {
            const surveyScope = getChannelLinkScope({ locationId: modal.locations[0] }, locations);
            const surveyLocation = locations.find(location => location.companyId === modal.locations[0]);
            filterCb = c =>
                canInitiateConversation(c) &&
                (assignsLiveChatLocation(c)
                    ? liveChatServesLocation(c, surveyLocation)
                    : isChannelInLinkScope(c, surveyScope));
        }
        return getChannelOptions(
            { in: true, out: true, contact: { phoneNumber: modal.ctx?.phoneNumber, email: modal.ctx?.email } },
            getAccessibleChannelIds(filterCb),
        );
    }, [channels, isSurveyContext, modal.locations, locations]);

    const whatsappTemplatesOptions = useMemo(() => {
        return getWhatsAppBusinessTemplatesOptionsByChannel(selectedChannelId, { hasButtons: false });
    }, [selectedChannelId, getWhatsAppBusinessTemplatesOptionsByChannel]);

    const onCloseModal = () =>
        dispatch(
            contactsActions.setContactInitConversationModal({ open: false, contactId: null, companies: [], ctx: null }),
        );

    const onInitConversation = async values => {
        try {
            const isEmail = channelType === CHANNEL.EMAIL;
            const omitsLocation =
                selectedChannel?.type === CHANNEL.LIVE_CHAT && !assignsLiveChatLocation(selectedChannel);
            const conversation = await conversationsService.create({
                ...values,
                locationId: omitsLocation ? null : values.locationId || null,
                message: channelType === CHANNEL.WHATSAPP_BUSINESS ? '' : parseTipTapJSONToPlainText(values.message),
                contactId: modal.contactId,
                ...(isEmail ? { subject: values.subject, htmlContent: parseTipTapJSONToHTML(values.message) } : {}),
            });
            dispatch(contactsActions.getContact(modal.contactId));
            if (onInitConversationCallback) {
                onInitConversationCallback();
            }
            dispatch(
                alertActions.success(t('alertMessages.conversationCreated'), {
                    actionPath: `${RoutePath.MessagingInbox}?conversationId=${conversation.id}`,
                    actionLabel: t('InitConversation.goToConversation'),
                }),
            );
            onCloseModal();
        } catch (error) {
            if (error.errorCode === 'open_conversation_exists') {
                setResolveModal({ open: true, conversations: error.conversations });
                return;
            }
            handleApiError(error, 'alertMessages.sendFail');
            if (error.errorCode === 'channel_configuration_error') {
                setChannelType(null);
                setValue('whatsAppTemplateId', '');
                setValue('channelId', '', { shouldDirty: true });
                dispatch(channelsActions.get());
            }
        }
    };

    const closeResolveModal = () => setResolveModal({ open: false, conversations: [] });

    const onGoToConversation = () => {
        navigate(`${RoutePath.MessagingInbox}?conversationId=${resolveModal.conversations[0]}`);
        onCloseModal();
    };

    const onResolveConversation = async () => {
        try {
            await conversationsService.editBulk({
                selectedConversations: resolveModal.conversations,
                property: ConversationProperty.Status,
                status: ConversationStatus.Closed,
                selectByFilter: false,
                filter: null,
            });
            dispatch(alertActions.success(t('alertMessages.updateSuccess')));
            closeResolveModal();
            dispatch(contactsActions.getContact(modal.contactId));
            if (onInitConversationCallback) {
                onInitConversationCallback();
            }
        } catch (error) {
            handleApiError(error);
        }
    };

    if (!hasAccess('messaging')) return null;

    return (
        <>
            <Modal
                fullScreen
                isOpen={modal?.open}
                handleClose={onCloseModal}
                isDirty={isDirty}
                title={t('InitConversation.newConversation')}
                onPrimaryAction={handleSubmit(onInitConversation)}
                primaryActionText={t('buttons.create')}
                primaryActionDisabled={isSubmitting || !isValid || !isDirty}
            >
                <Content>
                    <Controller
                        control={control}
                        name="contactId"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                label={t('InitConversation.recipient')}
                                fullWidth
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                                required
                                disabled
                                sx={{ gridArea: 'input1' }}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="channelId"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Select
                                {...field}
                                onChange={({ target: { value } }) => {
                                    const channel = channels.find(c => c.id === value);
                                    setChannelType(channel?.type || CHANNEL.SMS);
                                    setValue('channelId', value, { shouldValidate: true, shouldDirty: true });
                                    setValue('whatsAppTemplateId', '');
                                }}
                                ref={ref}
                                label={t('common.channel')}
                                fullWidth
                                options={channelsOptions}
                                required
                                style={{ gridArea: 'select1' }}
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                            />
                        )}
                    />
                    {(isSurveyContext || !autoSelectSingleLocation) && channelLocationsOptions.length > 0 && (
                        <Controller
                            control={control}
                            name="locationId"
                            render={({ field, fieldState: { error }, ref }) => (
                                <Select
                                    {...field}
                                    ref={ref}
                                    onChange={({ target: { value } }) => {
                                        setValue('locationId', value, { shouldValidate: true, shouldDirty: true });
                                    }}
                                    options={channelLocationsOptions}
                                    label={t('common.location')}
                                    fullWidth
                                    style={{ gridArea: 'select2' }}
                                    error={!!error}
                                    helperText={error ? t(error.message) : null}
                                    required={assignsLiveChatLocation(selectedChannel)}
                                    disabled={isSurveyContext}
                                    groupBased
                                />
                            )}
                        />
                    )}
                    {channelType === CHANNEL.EMAIL && (
                        <Controller
                            control={control}
                            name="subject"
                            render={({ field, fieldState: { error }, ref }) => (
                                <Input
                                    ref={ref}
                                    inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                    label={t('common.subject')}
                                    fullWidth
                                    required
                                    style={{ gridArea: 'subject' }}
                                    error={!!error}
                                    helperText={error ? t(error.message) : null}
                                />
                            )}
                        />
                    )}
                    {channelType === CHANNEL.WHATSAPP_BUSINESS && (
                        <Controller
                            control={control}
                            name="whatsAppTemplateId"
                            render={({ field, fieldState: { error }, ref }) => (
                                <Select
                                    {...field}
                                    ref={ref}
                                    onChange={({ target: { value } }) => {
                                        setValue('whatsAppTemplateId', value, {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                        });
                                    }}
                                    options={whatsappTemplatesOptions}
                                    required
                                    label={t('common.template')}
                                    fullWidth
                                    style={{ gridArea: 'input2' }}
                                    error={!!error}
                                    helperText={error ? t(error.message) : null}
                                />
                            )}
                        />
                    )}
                    {channelType !== CHANNEL.WHATSAPP_BUSINESS && (
                        <Controller
                            control={control}
                            name="message"
                            render={({ field, fieldState: { error }, ref }) => (
                                <div style={{ gridArea: 'input2' }}>
                                    <TextEditor
                                        {...field}
                                        ref={ref}
                                        label={t('common.message', { count: 1 })}
                                        required
                                        limit={MAX_LENGTH[channelType]}
                                        placeholderContext={PLACEHOLDER_CONTEXT.INBOX}
                                        initialValue={''}
                                        onChange={text =>
                                            setValue('message', text, {
                                                shouldValidate: true,
                                                shouldDirty: true,
                                            })
                                        }
                                        error={error}
                                        aiAssistant
                                        toolbar={
                                            <div style={{ display: 'flex', gap: '6px' }}>
                                                <PostDynamicVariables hasLocation={!!locationId} />
                                                <InboxTemplateMenu
                                                    content={field.value}
                                                    setContent={text =>
                                                        setValue('message', text, {
                                                            shouldValidate: true,
                                                            shouldDirty: true,
                                                        })
                                                    }
                                                    tags={{
                                                        firstName: modal.ctx?.firstName,
                                                        lastName: modal.ctx?.lastName,
                                                        locations: modal.locations,
                                                    }}
                                                />
                                                {couponsEnabled && (!!locationId || !!selectedChannel?.brandId) && (
                                                    <CouponsMenu
                                                        setMessage={text =>
                                                            setValue('message', text, {
                                                                shouldValidate: true,
                                                                shouldDirty: true,
                                                            })
                                                        }
                                                        context={{
                                                            locationId,
                                                            brandId: locationId ? null : selectedChannel?.brandId,
                                                            firstName: modal.ctx?.firstName,
                                                            lastName: modal.ctx?.lastName,
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        }
                                    />
                                </div>
                            )}
                        />
                    )}
                </Content>
            </Modal>
            {resolveModal.open && (
                <ModalConfirm
                    isOpen={resolveModal.open}
                    aboveModal
                    handleClose={closeResolveModal}
                    title={t('InitConversation.conversationExistsTitle')}
                    description={t('InitConversation.conversationExistsDescription')}
                    onPrimaryAction={onGoToConversation}
                    primaryActionText={t('InitConversation.goToConversation')}
                    onAdditionalAction={onResolveConversation}
                    additionalTextButton={t('InitConversation.resolveConversation')}
                    additionalButtonType="outlined"
                />
            )}
        </>
    );
};

export default InitConversation;
